<template>
  <div class="container mt-5">
    <!-- ฟอร์มคำนวณค่างวด -->
    <div class="row">
      <div class="col-md-6">
        <div class="mb-3">
          <label for="loanAmount" class="mb-1">จำนวนเงินที่ขอกู้ (บาท)</label>
          <input v-model="formattedLoanAmount" class="form-control" type="text" id="loanAmount" @input="formatAmount" />
        </div>

        <div class="mb-3">
          <label for="interestRate" class="mb-1">อัตราดอกเบี้ยเฉลี่ย 3 ปีแรก (% ต่อปี)</label>
          <input v-model.number="interestRate" class="form-control" type="number" id="interestRate" />
        </div>

        <div class="mb-3">
          <label for="loanTerm" class="mb-1">ระยะเวลาที่ขอกู้ (ปี)</label>
          <input v-model.number="loanTerm" class="form-control" type="number" id="loanTerm" :max="30" />
          <small class="text-secondary">*สูงสุดไม่ควรเกิน 30 ปี</small>
        </div>

        <div v-if="errorMessage" class="text-danger mt-1">
          <p>{{ errorMessage }}</p>
        </div>

        <div class="d-flex justify-content-end">
          <button class="btn btn-outline-secondary mb-3 w-50 m-2" type="button"
            @click="StartcalculateEMI">เริ่มใหม่</button>
          <button class="btn btn-primary mb-3 w-50 m-2" type="button" @click="calculateEMI">คำนวณ</button>
        </div>
      </div>

      <!-- ผลคำนวณ EMI -->
      <div class="col-md-6 align-self-center">
        <div class="card card-cal mb-3 text-white">
          <div class="text-center p-4">
            <h2 class="fw-bold mb-2">ผลการคำนวณ</h2>
            <p class="mb-1">จำนวนเงินผ่อนต่อเดือน</p>
            <h2 class="fw-bold result-number" :class="{ pop: animating }">
              {{ formatWithComma(displayEmi.toFixed(0)) }} บาท*
            </h2>
          </div>
        </div>
      </div>
    </div>

    <!-- หมายเหตุ -->
    <div class="p-5">
      <div class="fw-bold">หมายเหตุ</div>
      <div class="text-secondary"><small>- อัตราดอกเบี้ยที่ใช้ในการคำนวณ คือ อัตราดอกเบี้ย 5 %
          เป็นอัตราดอกเบี้ยเบื้องต้น</small></div>
      <div class="text-secondary"><small>- การคำนวณการกู้สินเชื่อเป็นเพียงการคำนวณแค่เบื้องต้นเท่านั้น
          ทั้งนี้ขึ้นอยู่กับการอนุมัติสินเชื่อตามหลักเกณฑ์ของทางธนาคาร</small></div>
      <div class="text-secondary"><small>-
          การคำนวณนี้เป็นวงเงินที่ประมาณการยอดเงินกู้ได้สูงสุดซึ่งขึ้นอยู่กับรายได้</small></div>
    </div>

    <!-- ส่วนหัวรายการ + ตัวเลือกเรียง -->
    <div class="row g-3" v-if="calculated">
      <div class="col-md-8">
        <h3 class="fw-bold">โครงการที่แนะนำ</h3>
        <div class="text-muted">
          ทั้งหมด <b>{{ filteredProjects.length }}</b> โครงการ
          • บ้านเดี่ยว <b>{{ filteredSingleHouse.length }}</b>
          • ทาวน์โฮม <b>{{ filteredTownhome.length }}</b>
        </div>
      </div>
      <div class="col-md-2 align-self-center">
        <div class="mb-3 text-end"><small>เรียงลำดับ</small></div>
      </div>
      <div class="col-md-2 text-end">
        <select class="form-select form-select-sm" v-model="sortOrder" aria-label="Sort">
          <option value="1">ราคาน้อย - มาก</option>
          <option value="2">ราคามาก - น้อย</option>
        </select>
      </div>
    </div>

    <!-- บ้านเดี่ยว -->
    <div v-if="calculated && filteredSingleHouse.length > 0" class="mt-4">
      <h4 class="fw-bold mb-3">บ้านเดี่ยว</h4>
      <div class="row">
        <div class="col-12 col-sm-6 col-md-6 col-lg-3" v-for="project in filteredSingleHouse" :key="`sh-${project.id}`">
          <a :href="project.project_url" target="_blank" class="text-decoration-none">
            <div class="card border-0 mb-3 singlehouse-card">
              <!-- รูปเต็ม -->
              <img :src="project.image" class="card-img-top project-img" alt="image" />

              <!-- ส่วนข้อมูล -->
              <div class="card-body project-info d-flex justify-content-between align-items-center text-white">
                <div>
                  <p class="mb-0">{{ project.brand }}</p>
                  <div class="mb-0 location">{{ project.location }}</div>
                </div>

                <div v-if="asNumber(project.list_price_min_million) > 0" class="text-end">
                  <small>ราคาเริ่มต้น (ลบ.)</small><br>
                  <b>{{ priceLabel(project) }}</b>
                </div>
              </div>
            </div>
          </a>
        </div>
      </div>
    </div>

    <!-- ทาวน์โฮม -->
    <div v-if="calculated && filteredTownhome.length > 0" class="mt-4">
      <h4 class="fw-bold mb-3">ทาวน์โฮม</h4>
      <div class="row">
        <div class="col-12 col-sm-6 col-md-6 col-lg-3" v-for="project in filteredTownhome" :key="`sh-${project.id}`">
          <a :href="project.project_url" target="_blank" class="text-decoration-none">
            <div class="card border-0 mb-3 singlehouse-card">
              <!-- รูปเต็ม -->
              <img :src="project.image" class="card-img-top project-img" alt="image" />

              <!-- ส่วนข้อมูล -->
              <div class="card-body project-info d-flex justify-content-between align-items-center text-white">
                <div>
                  <p class="mb-0">{{ project.brand }}</p>
                  <div class="location mb-0">{{ project.location }}</div>
                </div>

                <div v-if="asNumber(project.list_price_min_million) > 0" class="text-end">
                  <small>ราคาเริ่มต้น (ลบ.)</small><br>
                  <b>{{ priceLabel(project) }}</b>
                </div>
              </div>
            </div>
          </a>
        </div>
      </div>
    </div>

    <!-- ไม่มีผลลัพธ์ -->
    <div v-if="calculated && filteredProjects.length === 0" class="text-center text-muted my-5">
      ไม่พบโครงการที่อยู่ในงบประมาณของคุณ
    </div>
  </div>
</template>

<script>
import store from "@/store";
import { GET_BRITANIA_DETAILS } from "../store/actions.type";

export default {
  name: "CalculateTabs",
  data() {
    return {
      loanAmount: 3500000,
      interestRate: 5.3,
      loanTerm: 30,
      emi: null,             // ค่าจริงหลังคำนวณ
      displayEmi: 0,         // ตัวเลขที่โชว์ (นับจาก 0 ไปหา emi)
      animating: false,      // flag สำหรับเอฟเฟกต์
      errorMessage: "",
      formattedLoanAmount: "3,500,000",
      projects: [],
      sortOrder: "2",
      calculated: false
    };
  },
  async created() {
    await this.init();
  },
  computed: {
    // ทั้งหมดที่เข้าเงื่อนไขงบ (เทียบราคาขั้นต่ำ * 1,000,000 กับ loanAmount)
    filteredProjects() {
      const withinBudget = this.projects.filter((p) => {
        const minM = this.asNumber(p.list_price_min_million);
        return minM != null && minM * 1_000_000 <= this.loanAmount;
      });
      // เรียงตามราคาตาม sortOrder
      return [...withinBudget].sort((a, b) => {
        const aMin = this.asNumber(a.list_price_min_million) ?? Infinity;
        const bMin = this.asNumber(b.list_price_min_million) ?? Infinity;
        return this.sortOrder === "1" ? aMin - bMin : bMin - aMin;
      });
    },
    filteredSingleHouse() {
      return this.filteredProjects.filter((p) => (p.project_type || "").toLowerCase() === "single_house");
    },
    filteredTownhome() {
      return this.filteredProjects.filter((p) => (p.project_type || "").toLowerCase() === "townhome");
    },
  },
  methods: {
    async init() {
      const rows = await store.dispatch(GET_BRITANIA_DETAILS);
      this.projects = (rows || []).map((r) => ({
        id: Number(r.id),
        brand: r.brand || "",
        location: r.location || "",
        list_price_min_million: this.asNumber(r.list_price_min_million),
        list_price_max_million: this.asNumber(r.list_price_max_million),
        project_url: r.project_url || "",
        image: r.image || r.image_url || "",
        project_type: (r.project_type || "").toLowerCase(), // 'single_house' | 'townhome'
      }));
    },

    // EMI = ค่างวดต่อเดือน
    calculateEMI() {
      if (this.loanAmount <= 0 || this.interestRate <= 0 || this.loanTerm <= 0 || this.loanTerm > 30) {
        this.errorMessage = "กรุณาใส่ข้อมูลให้ครบถ้วน";
        this.calculated = false;
        return;
      }
      this.errorMessage = "";
      const P = this.loanAmount;
      const r = (this.interestRate / 100) / 12;
      const n = this.loanTerm * 12;
      this.emi = r > 0 ? (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1) : P / n;

      this.animateEmiCount(this.emi); // นับเลขพร้อมเอฟเฟกต์
      this.calculated = true;
    },

    // นับจาก 0 → target ด้วย easing
    animateEmiCount(target, duration = 800) {
      this.animating = true;
      this.displayEmi = 0;
      const start = performance.now();

      const tick = (now) => {
        const t = Math.min((now - start) / duration, 1);  // 0..1
        const eased = 1 - Math.pow(1 - t, 3);             // easeOutCubic
        this.displayEmi = target * eased;
        if (t < 1) requestAnimationFrame(tick);
        else { this.displayEmi = target; this.animating = false; }
      };
      requestAnimationFrame(tick);
    },

    // helpers
    asNumber(v) {
      if (v === null || v === undefined) return null;
      const n = typeof v === "number" ? v : parseFloat(String(v).replace(/[^\d.-]/g, ""));
      return isNaN(n) ? null : n;
    },
    priceLabel(p) {
      const minM = this.asNumber(p.list_price_min_million);
      const maxM = this.asNumber(p.list_price_max_million);
      if (minM != null && maxM != null && maxM > minM) return `${minM.toFixed(2)} - ${maxM.toFixed(2)}`;
      return minM != null ? minM.toFixed(2) : "-";
    },
    formatAmount() {
      let value = this.formattedLoanAmount.replace(/[^0-9]/g, "");
      value = value.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      this.formattedLoanAmount = value;
      this.loanAmount = parseInt(value.replace(/,/g, ""), 10) || 0;
    },
    formatWithComma(v) {
      return String(v).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
    StartcalculateEMI() {
      this.loanAmount = 3500000;
      this.interestRate = 5.3;
      this.loanTerm = 30;
      this.formattedLoanAmount = "3,500,000";
      this.emi = null;
      this.displayEmi = 0;   // รีเซ็ตให้โชว์ 0
      this.animating = false;
      this.errorMessage = "";
      this.calculated = false;
    },
  },
};
</script>

<style scoped>
.singlehouse-card,
.project-img,
.project-info {
  border-radius: 0 !important;
}

.singlehouse-card {
  overflow: hidden;
  /* border-radius: 8px; */
}

.project-img {
  width: 100%;
  /* height: 220px; */
  height: auto;
  object-fit: cover;
  /* ทำให้รูปเต็มพอดี */
}

.project-info {
  background-color: #de504f;
  color: #fff;
  padding: 10px 12px;
}

.project-info p {
  font-size: 14px;
}

.project-info b {
  font-size: 16px;
}

.result-number { transition: transform .25s ease, opacity .25s ease; }
.pop { animation: pop .5s ease; }
@keyframes pop {
  0%   { transform: scale(.9);  opacity:.6; }
  60%  { transform: scale(1.06); opacity:1; }
  100% { transform: scale(1); }
}

.location{
  font-size: 18px;
  font-weight: 400;
}
</style>
